import React, { useCallback, useRef, useState } from 'react';
import { Skeleton, message } from 'antd';
import styled from '@emotion/styled';
import useSWR from 'swr';
import { PER_PAGE_OTHER } from '../../../constants/common';
import { API_URLS } from '../../../constants/urls/api-urls';
import { getBenefitListRepository } from '../../../repository/mytm/benefit/benefitRepository';
import { getPartnerListRepository, getPartnersRepository } from '../../../repository/mytm/partner/partnerRepository';
import { BenefitPageHeader } from '../../organisms/benefit/BenefitPageHeader';
import { PartnerPageHeader } from '../../organisms/partner/PartnerPageHeader';
import { BenefitTable } from '../../organisms/benefit/BenefitTable';
import { PartnerTable } from '../../organisms/partner/PartnerTable';
import { PartnerType, BenefitType } from '../../../types/benefit';
import { FormBenefitModal } from '../../organisms/benefit/FormBenefitModal';
import { appStyles } from '../../../styles';
import { MESSAGE_ERROR } from '../../../constants/message';
import { FormPartnerModal } from '../../organisms/partner/FormPartnerModal';
import SelectButtons from '../../atoms/select-buttons/SelectButtons';
import { useCountrySelection } from '../../../hooks/useCountrySelection';

export const Benefit = () => {
  const [pageActive, setPageActive] = useState(1);
  const [pagePartner, setPagePartner] = useState(1);
  const [loading] = useState(false);
  const [loadingPartners] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isOpenModalPartner, setIsOpenModalPartner] = useState(false);
  const [record, setDataRecord] = useState<BenefitType>();
  const [recordPartner, setDataRecordPartner] = useState<BenefitType>();
  const errorCount = useRef(0);
  const [countrySelected, setCountrySelected] = useCountrySelection(null);

  const { data, isValidating, error, mutate } = useSWR(
    countrySelected ? [API_URLS.BENEFIT_LIST, pageActive, 1, countrySelected] : null,
    (url: string, pageNumber: number) =>
      getBenefitListRepository(url, {
        page: pageNumber,
        take: PER_PAGE_OTHER,
        country: countrySelected!,
      }),
    {
      revalidateOnFocus: false,
      refreshInterval: 600000,
    },
  );

  const swrPartnerAll: any = useSWR([API_URLS.PARTNER_API], (url: string) => getPartnersRepository(url), {
    revalidateOnFocus: false,
    refreshInterval: 600000,
  });

  const swrPartnerPaginate = useSWR(
    [API_URLS.PARTNER_LIST, pagePartner, 0],
    (url: string, pageNumber: number) =>
      getPartnerListRepository(url, {
        page: pageNumber,
        take: PER_PAGE_OTHER,
      }),
    {
      revalidateOnFocus: false,
      refreshInterval: 600000,
    },
  );

  const onCloseModal = useCallback(() => {
    setIsOpenModal(false);
  }, []);

  const onCloseModalPartner = useCallback(() => {
    setIsOpenModalPartner(false);
  }, []);

  const handleChangePageActive = useCallback(value => {
    setPageActive(value);
    window.scrollTo(0, 0);
  }, []);

  const handleChangePagePartner = useCallback(value => {
    setPagePartner(value);
  }, []);

  if (error || swrPartnerPaginate?.error) {
    errorCount.current += 1;
    if (errorCount.current === 1) {
      message.error(MESSAGE_ERROR.M02);
      const timeout = setTimeout(() => {
        errorCount.current = 0;
        clearTimeout(timeout);
      }, 5000);
    }
  }

  const recordBenefit = data?.benefits.map((item: BenefitType, index: number) => ({ ...item, ...{ index } }));
  const recordPartners = swrPartnerPaginate?.data?.partners.map((item: PartnerType, index: number) => ({
    ...item,
    ...{ index },
  }));
  const partnerAll = swrPartnerAll?.data;

  return (
    <Container>
      <BenefitPageHeader setIsOpenModal={setIsOpenModal} />
      <SelectButtons onChangeValue={(item: string) => setCountrySelected(item)} />
      {!data && (
        <Container>
          <Skeleton active />
        </Container>
      )}
      {data && (
        <BenefitTable
          handleChangePageActive={handleChangePageActive}
          onRefresh={mutate}
          records={recordBenefit || []}
          loading={loading || isValidating}
          setIsOpenModal={setIsOpenModal}
          setDataRecord={setDataRecord}
          pagination={{
            onChange: handleChangePageActive,
            pageSize: PER_PAGE_OTHER,
            current: pageActive,
            total: data?.total,
          }}
        />
      )}
      <PartnerPageHeader setIsOpenModal={setIsOpenModalPartner} />
      {recordPartners && (
        <PartnerTable
          handleChangePagePartner={handleChangePagePartner}
          onRefresh={swrPartnerPaginate.mutate}
          records={recordPartners || []}
          loading={loadingPartners || swrPartnerPaginate?.isValidating}
          setIsOpenModal={setIsOpenModalPartner}
          setDataRecord={setDataRecordPartner}
          pagination={{
            onChange: handleChangePagePartner,
            pageSize: PER_PAGE_OTHER,
            current: pagePartner,
            total: swrPartnerPaginate?.data?.total,
          }}
        />
      )}

      {isOpenModal && countrySelected && (
        <FormBenefitModal
          setDataRecord={setDataRecord}
          record={record}
          onRefresh={mutate}
          isOpenModal={isOpenModal}
          onClose={onCloseModal}
          partners={partnerAll}
          setIsOpenModalPartner={setIsOpenModalPartner}
          countrySelected={countrySelected}
        />
      )}

      {isOpenModalPartner && (
        <FormPartnerModal
          setDataRecord={setDataRecordPartner}
          record={recordPartner}
          onRefresh={swrPartnerPaginate.mutate}
          onRefreshPartnerAll={swrPartnerAll.mutate}
          onReFreshBenefit={mutate}
          isOpenModal={isOpenModalPartner}
          onClose={onCloseModalPartner}
        />
      )}
    </Container>
  );
};

const Container = styled.div`
  ${appStyles.paddingComponent}

  & .ant-pagination {
    float: right;
    padding: 15px 0 0 0;
  }

  .ant-table-thead {
    border-bottom: 2px solid #eee;
  }
  .ant-table-thead > tr > th {
    ${appStyles.background.tableBgGray}
    text-transform: uppercase;
    font-size: 0.75rem;
    font-weight: 600;
    &:before {
      display: none;
    }
  }

  & .ant-page-header {
    padding-left: 0px;
  }
`;
